import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TextCountDownStyled } from '../missions-render-v2/style-component';
import { CircularProgressPieProps, CountDownUpgradableProps } from './countdown-upgradable-type';
import { useTheme } from '@/contexts/theme-context/useThemeContext';

const CircularProgressPie: React.FC<CircularProgressPieProps> = ({
  percent,
  size = 200,
  bgColor = '#1837440D',
  progressColor = '#FF1C72'
}) => {
  const radius = size / 2;
  const progressAngle = (percent / 100) * 360;
  const rad = (angle: number) => (angle * Math.PI) / 180;

  const x = radius + radius * Math.cos(rad(progressAngle - 90));
  const y = radius + radius * Math.sin(rad(progressAngle - 90));

  const largeArcFlag = percent > 50 ? 1 : 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: 'hidden' }}>
      <circle cx={radius} cy={radius} r={radius} fill={bgColor} />
      <path
        d={`
          M ${radius} ${radius} 
          L ${radius} 0 
          A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x} ${y} 
          Z
        `}
        fill={progressColor}

      />
    </svg>
  );
};


const CountDownUpgradable: React.FC<CountDownUpgradableProps> = ({
  text = 'Espera',
  onExecute,
  targetMinute = 0,
  show = true,
  increment = 0
}) => {
  const newTargetMinute = targetMinute || parseInt(import.meta.env.VITE_TIME_TO_UPDATE_PROGRESS ?? 10);
  const { theme } = useTheme();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>("00:00");

  const calculateNextExecution = useCallback(() => {
    const now = new Date();
    const currentMinute = now.getMinutes();
    const totalInterval = newTargetMinute + increment;

    let nextMinute = Math.ceil(currentMinute / totalInterval) * totalInterval;
    if (nextMinute <= currentMinute) {
      nextMinute += totalInterval;
    }

    const overflow = Math.floor(nextMinute / 60);
    nextMinute = nextMinute % 60;
    const nextHour = (now.getHours() + overflow) % 24;

    const nextExecution = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      nextHour,
      nextMinute,
      0
    );

    if (nextExecution.getTime() < now.getTime()) {
      nextExecution.setDate(nextExecution.getDate() + 1);
    }

    const timeDiffInSeconds = (nextExecution.getTime() - now.getTime()) / 1000;

    return {
      hours: Math.floor(timeDiffInSeconds / 3600),
      minutes: Math.floor((timeDiffInSeconds % 3600) / 60),
      seconds: Math.floor(timeDiffInSeconds % 60)
    };
  }, [newTargetMinute, increment]);

  const startCountdown = useCallback(() => {
    const handleExecute = async () => {
      setTimeRemaining("00:00");
      if (onExecute && typeof onExecute === 'function') {
        await onExecute();
        startCountdown();
      }
    };

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      const { hours, minutes, seconds } = calculateNextExecution();

      setTimeRemaining(
        `${hours > 0 ? String(hours).padStart(2, "0") + ':' : ''}${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
      );

      if (hours === 0 && minutes === 0 && seconds === 0) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        handleExecute();
      }
    }, 1000);
  }, [calculateNextExecution, onExecute]);

  useEffect(() => {
    startCountdown();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [startCountdown]);


  const progressPercent = useMemo(() => {
    if (timeRemaining === "00:00") {
      return 0;
    }
    const { hours, minutes, seconds } = calculateNextExecution();  

    const totalTimeInSeconds = (newTargetMinute + increment) * 60;
    const remainingSeconds = (hours * 3600) + (minutes * 60) + seconds;
    

    const progress = ((totalTimeInSeconds - remainingSeconds) / totalTimeInSeconds) * 100;
    return Math.min(Math.max(progress, 0), 100);
  }, [calculateNextExecution, newTargetMinute, increment, timeRemaining]);

  if (!show) {
    return null;
  }

  return (
    <TextCountDownStyled>
      <span>{text}</span>
      <CircularProgressPie
        size={24}
        percent={progressPercent}
        bgColor={theme?.colors?.theme?.contentBgOpaque}
        progressColor={theme?.colors?.theme?.brandColor3}
      />
      <span>{timeRemaining}</span>
    </TextCountDownStyled>
  );
};

export default CountDownUpgradable;

